import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useModules } from "../../context/ModuleContext";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";
import { customizationsService } from "../../services/customizations";
import { getImageUrl } from "../../utils/imageUtils";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package, Search, Upload, X, Info, DollarSign, Image as ImageIcon, Settings, Star, ArrowLeft, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResellerProducts() {
    // eDisplayroom off: the catalog stays browsable so the funeral home can see
    // what it built, but every entry point into a write is removed. Hiding the
    // edit sheet's triggers is what makes its internal writes unreachable.
    const { canWrite } = useModules();
    const readOnly = !canWrite("edisplayroom");

    const [products, setProducts] = useState([]);
    const [customCollections, setCustomCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("general");
    const [editingProduct, setEditingProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Variant state (mirrors AdminProducts pattern)
    const [editingProductVariants, setEditingProductVariants] = useState([]);
    const [parentProductBeforeVariant, setParentProductBeforeVariant] = useState(null);
    const [parentProductName, setParentProductName] = useState("");

    // Pagination & Filters
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [filterCollection, setFilterCollection] = useState([]);
    const [filterAddon, setFilterAddon] = useState([]);

    const [allCustomizations, setAllCustomizations] = useState([]);

    // Form state — field names match the backend model exactly
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        price: "",
        image_url: "",
        images: [],
        sku: "",
        inventory_quantity: 0,
        customizations: { mandatory: [], optional: [] },
        is_addon: false,
        addons: [],
        collections: [],
        is_variant: false,
        parent_product_id: "",
        variant_name: "",
        is_active: true,
        is_available: true
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData();
        }, 300);
        return () => clearTimeout(handler);
    }, [page, limit, filterCollection, filterAddon, searchTerm]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                page,
                limit,
                search: searchTerm || undefined,
                only_parents: true,
                paginate: true
            };
            if (filterCollection.length > 0) params.collection_id = filterCollection.join(",");
            if (filterAddon.length === 1) params.is_addon = filterAddon[0] === "only_addons";

            const results = await Promise.allSettled([
                axios.get(`${API}/custom-products`, { params }),
                axios.get(`${API}/custom-collections`),
                customizationsService.getAll()
            ]);

            // Products
            if (results[0].status === 'fulfilled') {
                const resData = results[0].value.data;
                console.log('[ResellerProducts] API response:', resData);
                if (resData.data) {
                    setProducts(resData.data);
                    if (resData.pagination) {
                        setTotalPages(resData.pagination.totalPages);
                        setTotalItems(resData.pagination.total);
                        if (page > resData.pagination.totalPages && resData.pagination.totalPages > 0) {
                            setPage(resData.pagination.totalPages);
                        }
                    }
                } else {
                    setProducts(Array.isArray(resData) ? resData : (resData.products || []));
                }
            } else {
                console.error('[ResellerProducts] Products fetch failed:', results[0].reason);
                toast.error("Failed to fetch products");
            }

            // Collections
            if (results[1].status === 'fulfilled') {
                const colsData = results[1].value.data;
                const collectionsData = Array.isArray(colsData)
                    ? colsData
                    : (colsData.collections || colsData.data || []);
                setCustomCollections(collectionsData);
            }

            // Customizations
            if (results[2].status === 'fulfilled') {
                const custRes = results[2].value;
                setAllCustomizations((Array.isArray(custRes) ? custRes : []).filter(c => c.is_active).map(c => ({
                    label: `${c.name} ($${c.price})`,
                    value: c.customization_id
                })));
            }
        } catch (error) {
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    // Safe ID helpers
    const getProductId = (prod) => prod.custom_product_id || prod.product_id || prod._id;
    const getCollectionId = (col) => col.collection_id || col._id;

    // =================== CRUD ===================

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name?.trim()) return toast.error("Product name is required.");
        if (!formData.price) return toast.error("Price is required.");
        if (formData.is_variant && !formData.variant_name?.trim()) return toast.error("Variant name is required.");
        if (!formData.is_variant && (!formData.collections || formData.collections.length === 0)) {
            return toast.error("Please select at least one collection.");
        }

        try {
            const payload = {
                ...formData,
                base_price: parseFloat(formData.price),
                display_price: parseFloat(formData.price),
                inventory_quantity: parseInt(formData.inventory_quantity)
            };

            if (editingProduct) {
                const prodId = getProductId(editingProduct);
                await axios.put(`${API}/custom-products/${prodId}`, payload);
                toast.success("Product updated successfully");
            } else {
                await axios.post(`${API}/custom-products`, payload);
                toast.success(formData.is_variant ? "Variant created successfully" : "Product created successfully");
            }

            // Post-save navigation (mirrors AdminProducts pattern)
            if (formData.is_variant && formData.parent_product_id) {
                if (parentProductBeforeVariant) {
                    // Return to parent's variant tab
                    const parent = parentProductBeforeVariant;
                    setParentProductBeforeVariant(null);
                    handleEdit(parent);
                    setTimeout(() => setActiveTab("variants"), 0);
                } else {
                    fetchEditingProductVariants(formData.parent_product_id);
                    setParentProductName(null);
                    resetForm();
                    setActiveTab("variants");
                }
            } else {
                setDialogOpen(false);
                setEditingProduct(null);
                setParentProductName(null);
                resetForm();
                fetchData();
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to save product");
        }
    };

    const fetchEditingProductVariants = async (parentId) => {
        try {
            const response = await axios.get(`${API}/custom-products?parent_product_id=${parentId}`);
            const variantData = response.data.data || response.data.products || response.data || [];
            setEditingProductVariants(Array.isArray(variantData) ? variantData : []);
        } catch (error) {
            console.error("Failed to fetch custom variants", error);
            toast.error("Could not load product variants.");
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setParentProductName(null);

        // Fetch variants if editing a parent product
        if (!product.is_variant && !product.is_addon) {
            fetchEditingProductVariants(getProductId(product));
        }

        setFormData({
            name: product.name,
            description: product.description || "",
            price: product.base_price || product.display_price || product.price || "",
            image_url: product.image_url || "",
            images: product.images || [],
            sku: product.sku || "",
            inventory_quantity: product.inventory_quantity || 0,
            customizations: product.customizations || { mandatory: [], optional: [] },
            is_addon: product.is_addon || false,
            addons: product.addons || [],
            collections: product.collections || [],
            is_variant: product.is_variant || false,
            parent_product_id: product.parent_product_id || "",
            variant_name: product.variant_name || "",
            is_active: product.is_active !== undefined ? product.is_active : true,
            is_available: product.is_available !== undefined ? product.is_available : true
        });
        setDialogOpen(true);
        setTimeout(() => setActiveTab("general"), 0);
    };

    const handleDelete = async (productId, isVariant = false, parentId = null) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            await axios.delete(`${API}/custom-products/${productId}`);
            toast.success(isVariant ? "Variant deleted" : "Product deleted");
            if (isVariant && parentId) {
                fetchEditingProductVariants(parentId);
            } else {
                fetchData();
            }
        } catch (error) {
            toast.error("Failed to delete product");
        }
    };

    const resetForm = () => {
        setEditingProduct(null);
        setFormData({
            name: "",
            description: "",
            price: "",
            image_url: "",
            images: [],
            sku: "",
            inventory_quantity: 0,
            customizations: { mandatory: [], optional: [] },
            is_addon: false,
            addons: [],
            collections: [],
            is_variant: false,
            parent_product_id: "",
            variant_name: "",
            is_active: true,
            is_available: true
        });
        setParentProductBeforeVariant(null);
        setParentProductName("");
        setEditingProductVariants([]);
        setActiveTab("general");
    };

    const openNewDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    // =================== Helpers ===================

    const getCollectionName = (colId) => {
        const col = customCollections.find(c => String(getCollectionId(c)) === String(colId));
        return (col && col.is_active) ? col.name : null;
    };

    const availableAddons = products
        .filter(p => p.is_addon && getProductId(p) !== (editingProduct ? getProductId(editingProduct) : null))
        .map(p => ({
            label: `${p.name} ($${p.base_price || p.display_price || 0})`,
            value: getProductId(p)
        }));

    // =================== Render ===================

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="reseller-products">
            <ModuleReadOnlyNotice module="edisplayroom" />

            {/* Header & Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">My Products</h2>
                    <p className="text-slate-500">Manage your exclusive store products</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative w-full sm:w-auto">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <Input
                            placeholder="Search products..."
                            className="pl-9 w-full sm:w-64"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                    </div>

                    <div className="w-full sm:w-56">
                        <MultiSelect
                            options={customCollections.map(c => ({ label: c.name, value: getCollectionId(c) }))}
                            selected={filterCollection}
                            onChange={(val) => { setFilterCollection(val); setPage(1); }}
                            placeholder="Filter by collection"
                        />
                    </div>

                    <div className="w-full sm:w-56">
                        <MultiSelect
                            options={[
                                { label: "Standard Products", value: "standard" },
                                { label: "Add-ons Only", value: "only_addons" }
                            ]}
                            selected={filterAddon}
                            onChange={(val) => { setFilterAddon(val); setPage(1); }}
                            placeholder="Filter by type"
                        />
                    </div>

                    {!readOnly && (
                    <Button
                        onClick={openNewDialog}
                        className="bg-indigo-600 hover:bg-indigo-700 rounded-xl whitespace-nowrap w-full sm:w-auto"
                        data-testid="add-custom-product-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Product
                    </Button>
                    )}
                </div>
            </div>

            {/* =================== SHEET (Create/Edit Form) =================== */}
            <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
                <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
                    <SheetHeader>
                        <div className="flex items-center gap-2">
                            {formData.is_variant && parentProductBeforeVariant && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const parent = parentProductBeforeVariant;
                                        setParentProductBeforeVariant(null);
                                        handleEdit(parent);
                                        setTimeout(() => setActiveTab("variants"), 0);
                                    }}
                                    className="mr-2"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    Back to Parent
                                </Button>
                            )}
                            <SheetTitle>
                                {editingProduct
                                    ? "Edit Product"
                                    : formData.is_variant
                                        ? `Add Variant for ${parentProductName}`
                                        : "Add Custom Product"}
                            </SheetTitle>
                        </div>
                    </SheetHeader>

                    <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList className={cn("grid w-full", (editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) ? "grid-cols-5" : "grid-cols-4")}>
                                <TabsTrigger value="general">
                                    <Info className="w-4 h-4 mr-2 hidden sm:inline" />
                                    General
                                </TabsTrigger>
                                <TabsTrigger value="pricing">
                                    <DollarSign className="w-4 h-4 mr-2 hidden sm:inline" />
                                    Pricing
                                </TabsTrigger>
                                <TabsTrigger value="images">
                                    <ImageIcon className="w-4 h-4 mr-2 hidden sm:inline" />
                                    Images
                                </TabsTrigger>
                                <TabsTrigger value="custom">
                                    <Settings className="w-4 h-4 mr-2 hidden sm:inline" />
                                    Custom
                                </TabsTrigger>
                                {(editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) && (
                                    <TabsTrigger value="variants">
                                        <Layers className="w-4 h-4 mr-2 hidden sm:inline" />
                                        Variants
                                    </TabsTrigger>
                                )}
                            </TabsList>

                            {/* ===== GENERAL TAB ===== */}
                            <TabsContent value="general" className="space-y-4 mt-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {formData.is_variant && (
                                        <div className="space-y-2">
                                            <Label htmlFor="variant_name">Variant Name <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="variant_name"
                                                required
                                                value={formData.variant_name}
                                                onChange={(e) => setFormData(prev => ({ ...prev, variant_name: e.target.value }))}
                                                placeholder="e.g., Red, Large, Oak..."
                                                className="h-12 border-amber-200 bg-amber-50/30"
                                            />
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <Label htmlFor="prod_name">{formData.is_variant ? "Parent Name" : "Name"} <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="prod_name"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className={cn("h-12", formData.is_variant && "bg-slate-50 text-slate-500 cursor-not-allowed")}
                                            placeholder="Product Name"
                                            disabled={formData.is_variant}
                                        />
                                    </div>
                                </div>

                                <div className={cn("grid gap-4", !formData.is_variant ? "grid-cols-1" : "grid-cols-1")}>
                                    <div className="space-y-2">
                                        <Label htmlFor="prod_sku">SKU</Label>
                                        <Input
                                            id="prod_sku"
                                            value={formData.sku}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                                            className="h-12"
                                        />
                                    </div>
                                </div>

                                {!formData.is_variant && (
                                    <div className="space-y-2">
                                        <Label htmlFor="prod_description">Description</Label>
                                        <Textarea
                                            id="prod_description"
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            rows={4}
                                        />
                                    </div>
                                )}

                                {!formData.is_variant && (
                                    <div className="space-y-2">
                                        <Label>Collections</Label>
                                        <MultiSelect
                                            options={customCollections.map(c => ({ label: c.name, value: getCollectionId(c) }))}
                                            selected={formData.collections || []}
                                            onChange={(selected) => setFormData(prev => ({ ...prev, collections: selected }))}
                                            placeholder="Select collections..."
                                        />
                                        <p className="text-xs text-slate-500">Assign this product to one or more collections.</p>
                                    </div>
                                )}

                                {/* Status & Add-on toggles */}
                                <div className="space-y-4 pt-4 border-t mt-4">
                                    <div className="flex items-center space-x-6">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="is_active"
                                                checked={formData.is_active}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                                            />
                                            <Label htmlFor="is_active" className="cursor-pointer">Visible in Store</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="is_available"
                                                checked={formData.is_available}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_available: checked }))}
                                            />
                                            <Label htmlFor="is_available" className="cursor-pointer">Available (Purchasable)</Label>
                                        </div>
                                    </div>

                                    {!formData.is_variant && (
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="is_addon"
                                                checked={formData.is_addon}
                                                onCheckedChange={(checked) => setFormData(prev => ({
                                                    ...prev,
                                                    is_addon: checked,
                                                    addons: checked ? [] : prev.addons
                                                }))}
                                            />
                                            <Label htmlFor="is_addon" className="cursor-pointer">Mark as Add On Product</Label>
                                        </div>
                                    )}

                                    {!formData.is_addon && !formData.is_variant && (
                                        <div className="space-y-2">
                                            <Label>Add On(s)</Label>
                                            <MultiSelect
                                                options={availableAddons}
                                                selected={formData.addons || []}
                                                onChange={(selected) => setFormData(prev => ({ ...prev, addons: selected }))}
                                                placeholder="Select add-on products..."
                                            />
                                            <p className="text-xs text-slate-500">Products that can be added to this item.</p>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            {/* ===== PRICING TAB ===== */}
                            <TabsContent value="pricing" className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="prod_price">Price ($)</Label>
                                        <Input
                                            id="prod_price"
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.price}
                                            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                            className="h-12"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="prod_inventory">Inventory</Label>
                                        <Input
                                            id="prod_inventory"
                                            type="number"
                                            value={formData.inventory_quantity}
                                            onChange={(e) => setFormData(prev => ({ ...prev, inventory_quantity: e.target.value }))}
                                            className="h-12"
                                        />
                                    </div>
                                </div>
                            </TabsContent>

                            {/* ===== IMAGES TAB ===== */}
                            <TabsContent value="images" className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Product Images</Label>
                                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files);
                                                if (files.length === 0) return;

                                                const fd = new FormData();
                                                files.forEach(file => fd.append('images', file));

                                                try {
                                                    const res = await axios.post(`${API}/upload/product-images`, fd, {
                                                        headers: { 'Content-Type': 'multipart/form-data' }
                                                    });
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        images: [...prev.images, ...res.data.urls],
                                                        image_url: prev.image_url || res.data.urls[0]
                                                    }));
                                                    toast.success("Images uploaded!");
                                                } catch (err) {
                                                    toast.error("Upload failed");
                                                }
                                            }}
                                        />
                                        <div className="flex flex-col items-center gap-2 text-slate-500">
                                            <Upload className="w-8 h-8" />
                                            <p className="text-sm font-medium">Click or drag images here</p>
                                        </div>
                                    </div>

                                    {formData.images && formData.images.length > 0 && (
                                        <div className="grid grid-cols-4 gap-2 mt-2">
                                            {formData.images.map((img, idx) => (
                                                <div key={idx} className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${formData.image_url === img ? 'border-indigo-600 ring-2 ring-indigo-100' : 'border-slate-200'}`}>
                                                    <img src={getImageUrl(img)} alt="Product" className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({ ...prev, image_url: img }))}
                                                        className={`absolute top-1 left-1 p-1 rounded-full transition-all ${formData.image_url === img ? 'bg-indigo-600 text-white opacity-100' : 'bg-white/90 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600'}`}
                                                        title={formData.image_url === img ? "Cover Image" : "Set as Cover"}
                                                    >
                                                        <Star className={`w-3 h-3 ${formData.image_url === img ? 'fill-current' : ''}`} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({
                                                            ...prev,
                                                            images: prev.images.filter((_, i) => i !== idx),
                                                            image_url: prev.image_url === img ? (prev.images.length > 1 ? prev.images.find((_, i) => i !== idx) || "" : "") : prev.image_url
                                                        }))}
                                                        className="absolute top-1 right-1 bg-white/90 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            {/* ===== CUSTOM TAB ===== */}
                            <TabsContent value="custom" className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Mandatory Customization</Label>
                                    <Select
                                        value={formData.customizations?.mandatory?.[0] || "none"}
                                        onValueChange={(value) => setFormData(prev => ({
                                            ...prev,
                                            customizations: {
                                                ...prev.customizations,
                                                mandatory: value === "none" ? [] : [value]
                                            }
                                        }))}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select mandatory customization..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None (No mandatory customization)</SelectItem>
                                            {allCustomizations.map((custom) => (
                                                <SelectItem key={custom.value} value={custom.value}>
                                                    {custom.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">Only one mandatory customization can be added. Customer MUST configure this.</p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Optional Customization</Label>
                                    <Select
                                        value={formData.customizations?.optional?.[0] || "none"}
                                        onValueChange={(value) => setFormData(prev => ({
                                            ...prev,
                                            customizations: {
                                                ...prev.customizations,
                                                optional: value === "none" ? [] : [value]
                                            }
                                        }))}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select optional customization..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None (No optional customization)</SelectItem>
                                            {allCustomizations.map((custom) => (
                                                <SelectItem key={custom.value} value={custom.value}>
                                                    {custom.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">Only one optional customization can be added. Customer CAN select this if desired.</p>
                                </div>
                            </TabsContent>

                            {/* ===== VARIANTS TAB ===== */}
                            {(editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) && (
                                <TabsContent value="variants" className="space-y-4 mt-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <Label className="text-base font-semibold">Product Variants</Label>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setFormData({
                                                    name: editingProduct.name,
                                                    description: editingProduct.description || "",
                                                    price: editingProduct.base_price || editingProduct.display_price || editingProduct.price || 0,
                                                    image_url: editingProduct.image_url || "",
                                                    images: editingProduct.images || [],
                                                    sku: (editingProduct.sku || "SKU") + "-VAR",
                                                    inventory_quantity: 0,
                                                    customizations: editingProduct.customizations || { mandatory: [], optional: [] },
                                                    is_addon: false,
                                                    addons: editingProduct.addons || [],
                                                    collections: editingProduct.collections || [],
                                                    is_variant: true,
                                                    parent_product_id: getProductId(editingProduct),
                                                    variant_name: "",
                                                    is_active: true,
                                                    is_available: true
                                                });
                                                setParentProductBeforeVariant(editingProduct);
                                                setEditingProduct(null);
                                                setParentProductName(editingProduct.name);
                                                setActiveTab("general");
                                            }}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Create New Variant
                                        </Button>
                                    </div>

                                    <div className="border rounded-md">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Variant Name</TableHead>
                                                    <TableHead>SKU</TableHead>
                                                    <TableHead>Price</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {editingProductVariants.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} className="text-center py-4 text-slate-500">
                                                            No variants found.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    editingProductVariants.map(variant => (
                                                        <TableRow key={getProductId(variant)}>
                                                            <TableCell>
                                                                <div className="font-medium text-slate-900">{variant.variant_name || 'N/A'}</div>
                                                                <div className="text-xs text-slate-500">{variant.name}</div>
                                                            </TableCell>
                                                            <TableCell>{variant.sku || '—'}</TableCell>
                                                            <TableCell>${Number(variant.base_price || variant.display_price || variant.price || 0).toFixed(2)}</TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setParentProductBeforeVariant(editingProduct);
                                                                            handleEdit(variant);
                                                                        }}
                                                                    >
                                                                        <Pencil className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDelete(getProductId(variant), true, getProductId(editingProduct))}
                                                                        className="text-red-600"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </TabsContent>
                            )}
                        </Tabs>

                        <div className="pt-4 mt-4 border-t sticky bottom-0 bg-white">
                            <Button
                                type="submit"
                                className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl h-12"
                            >
                                {editingProduct ? "Update Product" : "Create Product"}
                            </Button>
                        </div>
                    </form>
                </SheetContent>
            </Sheet>

            {/* =================== PRODUCT TABLE =================== */}
            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-semibold">Product</TableHead>
                            <TableHead className="font-semibold">Collections</TableHead>
                            <TableHead className="font-semibold">Price</TableHead>
                            <TableHead className="font-semibold">Inventory</TableHead>
                            <TableHead className="font-semibold text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {products.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                    No products found. Add one to get started.
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((product) => {
                                const prodId = getProductId(product);
                                return (
                                    <TableRow key={prodId} className="table-row">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {product.image_url ? (
                                                    <img
                                                        src={getImageUrl(product.image_url)}
                                                        alt={product.name}
                                                        className="w-10 h-10 rounded-lg object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                        <Package className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        {product.is_addon && (
                                                            <Badge variant="outline" className="text-[10px] text-indigo-600 border-indigo-200 bg-indigo-50">Add On</Badge>
                                                        )}
                                                        {!product.is_active && (
                                                            <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200 bg-slate-50">Hidden</Badge>
                                                        )}
                                                    </div>
                                                    <p className="font-medium text-slate-900">{product.name}</p>
                                                    {product.sku && (
                                                        <p className="text-xs text-slate-500 font-mono">{product.sku}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {product.collections && product.collections.length > 0 ? (
                                                    product.collections
                                                        .map(colId => getCollectionName(colId))
                                                        .filter(name => name !== null)
                                                        .map((name, idx) => (
                                                            <Badge key={idx} variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-100">
                                                                {name}
                                                            </Badge>
                                                        ))
                                                ) : null}
                                                {(!product.collections || product.collections.length === 0 || product.collections.every(id => !getCollectionName(id))) && (
                                                    <span className="text-slate-400 text-xs italic">Unassigned</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">${Number(product.base_price || product.display_price || product.price || 0).toFixed(2)}</TableCell>
                                        <TableCell>{product.inventory_quantity}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                {!readOnly && (<>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEdit(product)}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(prodId)}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                                </>)}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* =================== PAGINATION =================== */}
            {totalItems > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 animate-fade-in pb-8">
                    <div className="text-sm text-slate-500">
                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalItems)} of {totalItems} results
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Rows per page:</span>
                            <Select
                                value={limit.toString()}
                                onValueChange={(val) => { setLimit(Number(val)); setPage(1); }}
                            >
                                <SelectTrigger className="h-8 w-[70px]">
                                    <SelectValue placeholder={limit} />
                                </SelectTrigger>
                                <SelectContent>
                                    {[10, 15, 25, 50, 100].map(size => (
                                        <SelectItem key={size} value={size.toString()}>
                                            {size}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                Previous
                            </Button>

                            <div className="flex items-center gap-1 px-2">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                                    if (pageNum === 1 || pageNum === totalPages || (pageNum >= page - 1 && pageNum <= page + 1)) {
                                        return (
                                            <Button
                                                key={pageNum}
                                                variant={page === pageNum ? "default" : "outline"}
                                                size="sm"
                                                className={cn(
                                                    "h-8 w-8 p-0",
                                                    page === pageNum ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "text-slate-600 hover:bg-slate-100"
                                                )}
                                                onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </Button>
                                        );
                                    }
                                    if (pageNum === page - 2 || pageNum === page + 2) {
                                        return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
